import { MutationOptions, Observable, fromPromise } from '@apollo/client';
import { onError } from '@apollo/client/link/error';

import { Mutation, MutationRefreshTokenArgs } from './__generated__/graphql';
import { REFRESH_TOKEN_MUTATION } from './mutations';

import history from '@/utils/history';
import { stringToBase64 } from '@/utils/stringUtils';
import { logError } from '@/utils/logError';
import { clearAuthStorage } from '@/utils/authStorage';

import { client } from '.';
import { TerminatingErrors, RetryErrors } from '@/utils/errorUtils';

const messageContainsErrors = (message: string, errors: Record<string, string>) => (
  Object.values(errors).some((error) => message.includes(error))
);

const logUserOut = () => {
  const hadToken = !!localStorage.getItem('token');
  clearAuthStorage();
  client.stop();
  client.clearStore()
    .then(() => {
      if (window.location.pathname !== 'login' && hadToken) {
        history.replace('/login', { referrer: window.location.pathname });
      }
    });
};

export const networkErrorLink = onError(({
  networkError,
  operation,
}) => {
  if (networkError) {
    logError(
      '[GraphQL]',
      'network error',
      networkError,
      {
        level: 'error',
        tags: { graphql: 'network_error' },
        extra: { operationName: operation.operationName },
      },
    );
    logUserOut();
  }
});

export const terminatingErrorLink = onError(({
  graphQLErrors,
  operation,
}) => {
  const terminatingError = graphQLErrors?.find(({ message }) => (
    messageContainsErrors(message, TerminatingErrors)
  ));

  if (terminatingError) {
    logError(
      '[GraphQL]',
      `terminating error: ${terminatingError.message}`,
      undefined,
      {
        level: 'warning',
        tags: { graphql: 'terminating_error' },
        extra: {
          operationName: operation.operationName,
          errorMessage: terminatingError.message,
          allErrors: graphQLErrors?.map(({ message }) => message),
        },
      },
    );
    logUserOut();
  }
});

const refreshExpiredToken = async (cachedRefreshToken: string) => {
  const mutationOptions: MutationOptions<Mutation, MutationRefreshTokenArgs> = {
    mutation: REFRESH_TOKEN_MUTATION,
    variables: {
      refreshToken: cachedRefreshToken,
    },
  };
  const { data, errors } = await client.mutate(mutationOptions);
  if (errors?.length) {
    return null;
  }
  const { token, refreshToken } = data?.refreshToken;
  if (token) {
    localStorage.setItem('token', token);
  }
  if (refreshToken) {
    localStorage.setItem('refreshToken', refreshToken);
  }
  localStorage.setItem('tokenCreatedAt', stringToBase64(`${(new Date()).getTime()}`));
  return token;
};

export const retryErrorLink = onError(({
  graphQLErrors,
  operation,
  forward,
}) => {
  const hasRetryError = graphQLErrors?.some(({ message }) => (
    messageContainsErrors(message, RetryErrors)
  ));

  if (hasRetryError) {
    const cachedRefreshToken = localStorage.getItem('refreshToken');
    if (cachedRefreshToken) {
      const refreshTokenObservable = fromPromise(refreshExpiredToken(cachedRefreshToken));
      return refreshTokenObservable.flatMap((token) => {
        if (!token) {
          logError(
            '[GraphQL]',
            'token refresh returned null, logging out',
            undefined,
            {
              level: 'warning',
              tags: { graphql: 'retry_error' },
              extra: {
                operationName: operation.operationName,
                errors: graphQLErrors?.map(({ message }) => message),
              },
            },
          );
          logUserOut();
          return Observable.of();
        }
        const previousHeaders = operation.getContext().headers;
        operation.setContext({
          headers: {
            ...previousHeaders,
            authorization: `JWT ${token}`,
          },
        });
        return forward(operation);
      });
    }
    logError(
      '[GraphQL]',
      'no refresh token, logging out',
      undefined,
      {
        level: 'warning',
        tags: { graphql: 'retry_error' },
        extra: {
          operationName: operation.operationName,
          errors: graphQLErrors?.map(({ message }) => message),
        },
      },
    );
    logUserOut();
  }
  return null;
});

export const graphQLErrorLogLink = onError(({
  graphQLErrors,
  operation,
}) => {
  if (!graphQLErrors?.length) { return; }

  const unhandledErrors = graphQLErrors.filter(({ message }) => (
    !messageContainsErrors(message, TerminatingErrors)
    && !messageContainsErrors(message, RetryErrors)
  ));

  unhandledErrors.forEach(({ message, locations, path }) => {
    logError(
      '[GraphQL]',
      message,
      undefined,
      {
        level: 'error',
        tags: { graphql: 'unhandled_error' },
        extra: {
          operationName: operation.operationName,
          message,
          locations,
          path,
        },
      },
    );
  });
});
